import { loadSelectedOrderLabels } from '../../helpers/SelectedOrderLabels';
import { getOrderLabelErrorMessage } from '../../helpers/OrderLabelPreview';
import { resolveOrderSelectionRow, isOrderRowSelectable } from '../../helpers/orderSelection';
import React, { useState, useEffect, useRef } from "react";
import { useGetodersByFilterMutation } from "../../app/orderApi/OrderApi";
import Datatable from "../../components/shared/datatable/Datatable";
import { getClassByType } from "../../helpers/Utils";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import OrderFilters from "./OrderFilters";
import Loader from "../../components/shared/loader/Loader";
import { setPage as setOrdersPage, clearOrdersRealtimeUnseen, markOrdersLocallySeen, setOrdersListCache } from "../../app/orderApi/filterSlice";
import { useDispatch } from "react-redux";
import { useMarkOrderSeenMutation } from "../../app/orderApi/OrderApi";
import CountryFlag from "react-country-flag";
import { showToast } from "../../helpers/Utils";
import { useAuth } from "../auth/AuthContext";
import { hasRole } from "../../helpers/roleNames";
import { P } from "../../utils/permissions";
import {
  formatBundleDisplayLabel,
  getOrderBundleCode,
  resolveVaProviderName,
} from "../../helpers/vaInvoiceUtils";

// Use original order dates across sibling lines so a redo cannot move the creation date.
function getOrderCreatedDate(row, allItems) {
  const siblings = allItems.filter(item =>
    String(item.ShopifyId ?? '') === String(row.ShopifyId ?? '') &&
    String(item.BackerID ?? '') === String(row.BackerID ?? '')
  );
  const timestamps = [row, ...siblings]
    .map(item => item.OrderCreatedAt ? new Date(item.OrderCreatedAt).getTime() : NaN)
    .filter(Number.isFinite);
  return timestamps.length ? new Date(Math.min(...timestamps)) : null;
}

function formatOrderCreatedDate(date) {
  if (!date) return '—';
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function buildOrdersListQueryKey({ page, type, search, activeTab }) {
  return JSON.stringify({
    page: Number(page) || 1,
    type: type || "all",
    activeTab: activeTab || "all",
    search: search && typeof search === "object" ? search : {},
  });
}

function isActiveOrderItem(item) {
  if (!item) return false;
  if (item.softDeleted === true) return false;
  if (item.isRemoved || item.isReturned) return false;
  if (Number(item.Status) === 999) return false;
  if (item.Redo) return false;
  return true;
}

function mergeMobileOrdersPayload(previous, next) {
  if (!previous) return next;
  const mergeUnique = (left = [], right = [], key) => {
    const merged = new Map();
    [...left, ...right].forEach((item, index) => {
      const value = item?.[key] ?? item?.ItemID ?? item?.ShopifyId ?? index;
      merged.set(String(value), item);
    });
    return [...merged.values()];
  };
  return {
    ...previous,
    ...next,
    orders: mergeUnique(previous.orders, next?.orders, "ItemID"),
    uniqueOrders: mergeUnique(previous.uniqueOrders, next?.uniqueOrders, "BackerID"),
    pagination: next?.pagination || previous.pagination,
  };
}

function getBundleNamesForRow(row, allOrderItems = []) {
  const items = allOrderItems.filter(
    (item) => String(item.BackerID) === String(row?.BackerID)
  );
  const pool = items.length ? items : row ? [row] : [];
  const hasAnyActive = pool.some((item) => isActiveOrderItem(item));
  const candidates = hasAnyActive
    ? pool.filter((item) => isActiveOrderItem(item))
    : pool;

  const labels = [];
  const seen = new Set();
  for (const item of candidates) {
    const code = getOrderBundleCode(item);
    if (!code || seen.has(code)) continue;
    seen.add(code);
    const label = formatBundleDisplayLabel(code) || code;
    if (label) labels.push(label);
  }

  if (!labels.length) {
    const code = getOrderBundleCode(row);
    if (code) {
      const label = formatBundleDisplayLabel(code) || code;
      if (label) labels.push(label);
    }
  }

  return labels;
}

function getCaptifyVersionLabel(row, allOrderItems = []) {
  const bundleNames = getBundleNamesForRow(row, allOrderItems);
  if (bundleNames.length) {
    return bundleNames.join(", ");
  }

  const items = allOrderItems.filter(
    (item) => String(item.BackerID) === String(row?.BackerID)
  );
  const activeFrames = items.filter(
    (item) => item.ProductType?.toLowerCase() === "frame" && isActiveOrderItem(item)
  );
  if (activeFrames.length > 1) return "Multiple Pair";

  const hasAnyActive = items.some((item) => isActiveOrderItem(item));

  const versionItem = hasAnyActive
    ? activeFrames[0] || items.find((item) => isActiveOrderItem(item))
    : items.find((item) => item.ProductType?.toLowerCase() === "frame") ||
      items[0];

  const version = versionItem?.CaptifyVersion || "";
  if (!version || version === "Unknown") return "";
  return version;
}

function getPoNumberForRow(row, allOrderItems = []) {
  const fromRow = String(
    row?.invoicePoNumber || row?.originalPoNumber || row?.poNumber || ""
  ).trim();
  if (fromRow) return fromRow;

  const shopifyId = row?.ShopifyId != null ? String(row.ShopifyId) : "";
  const backerId = row?.BackerID != null ? String(row.BackerID) : "";
  const sibling = (allOrderItems || []).find((item) => {
    const sameOrder =
      (shopifyId && String(item?.ShopifyId) === shopifyId) ||
      (backerId && String(item?.BackerID) === backerId);
    if (!sameOrder) return false;
    return Boolean(
      String(
        item?.invoicePoNumber || item?.originalPoNumber || item?.poNumber || ""
      ).trim()
    );
  });
  return String(
    sibling?.invoicePoNumber || sibling?.originalPoNumber || sibling?.poNumber || ""
  ).trim();
}

function getProviderNameForRow(row, allOrderItems = []) {
  const fromRow = resolveVaProviderName(row);
  if (fromRow) return fromRow;

  const shopifyId = row?.ShopifyId != null ? String(row.ShopifyId) : "";
  const backerId = row?.BackerID != null ? String(row.BackerID) : "";
  for (const item of allOrderItems || []) {
    const sameOrder =
      (shopifyId && String(item?.ShopifyId) === shopifyId) ||
      (backerId && String(item?.BackerID) === backerId);
    if (!sameOrder) continue;
    const name = resolveVaProviderName(item);
    if (name) return name;
  }
  return "";
}

function getDemoOptionLabel(code) {
  const normalized = String(code || "")
    .trim()
    .toUpperCase()
    .replace(/[-\s]/g, "")
    .replace(/_BUNDLE$/, "");
  switch (normalized) {
    case "CAP1":
      return "CAP-1";
    case "CAP2":
      return "CAP-2";
    case "JUST_GLASSES":
      return "Just Glasses";
    case "PROCESSOR_ACCESSORY_KIT":
      return "Processor & Accessory Kit";
    default:
      return "Demo unit";
  }
}

function isDemoOrderRow(row, allOrderItems = []) {
  if (row?.isDemo === true) return true;
  return allOrderItems.some(
    (item) =>
      String(item.BackerID) === String(row?.BackerID) && item.isDemo === true
  );
}

const OrdersList = ({
  type,
  setShowFilter,
  setOrders,
  onLoadingChange,
  activeTab,
  removeSelection,
  variant = "admin",
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const [selectedRow, setSelectedRow] = useState(false);
  const [markOrderSeen] = useMarkOrderSeenMutation();
  const { hasAnyPermission, roles, canSoftDelete } = useAuth();
  const showApprovalStatusColumn =
    variant === "distributor" ||
    hasRole(roles, "superadmin") ||
    hasRole(roles, "va-admin");
  const showItemsAndCountryColumns = hasRole(roles, "superadmin");
  // Listing column header: superadmin → Name; va-admin / va-distributor → Veteran.
  const nameColumnLabel = hasRole(roles, "superadmin")
    ? "Name"
    : variant === "distributor" ||
        hasRole(roles, "va-admin") ||
        hasRole(roles, "va-distributor")
      ? "Veteran"
      : "Name";
  const canViewOrders = hasAnyPermission(
    P.ORDERS_READ_ALL,
    P.ORDERS_READ_VA,
    P.ORDERS_READ_ASSIGNED,
    P.ORDERS_READ_OWN
  );
  const canBulkSelect =
    hasAnyPermission(
      P.ORDERS_UPDATE_STATUS,
      P.ORDERS_REDO,
      P.ORDERS_EXPORT,
      P.ORDERS_SEND_TRACKING_MAIL,
      P.PRESCRIPTION_SEND_MAIL
    ) || Boolean(canSoftDelete);
  const [prevSearch, setPrevSearch] = useState(null);
  const [prevType, setPrevType] = useState(type);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleChange = (event) => setIsMobile(event.matches);
    mediaQuery.addEventListener?.("change", handleChange);
    return () => mediaQuery.removeEventListener?.("change", handleChange);
  }, []);
  const savedPage = useSelector((state) => state.filters.page);
  const realtimeVersion = useSelector((state) => state.filters.realtimeVersion);
  const ordersListCache = useSelector((state) => state.filters.ordersListCache);
  const { search } = useSelector((state) => state.filters);
  const realtimeUnseenIds = useSelector(
    (state) => state.filters.realtimeUnseenIds || []
  );
  const realtimeSeenIds = useSelector(
    (state) => state.filters.realtimeSeenIds || []
  );
  const pageFromUrl = Number(query.get("page")) || 0;
  const initialPage = isMobile ? 1 : (pageFromUrl || savedPage || 1);
  const cachedOnMount =
    ordersListCache?.queryKey ===
    buildOrdersListQueryKey({
      page: initialPage,
      type,
      search,
      activeTab,
    })
      ? ordersListCache.payload
      : null;
  const [page, setPage] = useState(initialPage);
  // Last successful API payload — socket refresh updates this without blanking the table.
  const [listPayload, setListPayload] = useState(cachedOnMount);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(Boolean(cachedOnMount));
  const [isFilterFetching, setIsFilterFetching] = useState(false);
  const silentInFlightRef = useRef(0);
  const labelPrintInFlight = useRef(false);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    dispatch(setOrdersPage(newPage));
    const params = new URLSearchParams(location.search);
    if (!isMobile && newPage !== 1) {
      params.set("page", String(newPage));
    } else {
      params.delete("page");
    }
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  useEffect(() => {
    if (!isMobile) return;
    setPage(1);
    dispatch(setOrdersPage(1));
  }, [dispatch, isMobile]);

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [selectedItemsIds, setSelectedItemsIds] = useState([]);
  const [selectedBackerID, setSelectedBackerID] = useState([]);
  const [selectedShopifyId, setSelectedShopifyId] = useState([]);

  const [getOrders] = useGetodersByFilterMutation();
  const [getOrdersForPrint] = useGetodersByFilterMutation();

  // Keep parent in sync when hydrating from cache on return from details
  useEffect(() => {
    if (cachedOnMount) {
      setOrders(cachedOnMount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Loader only for first load / filter changes — never for socket silent refresh.
  const showListLoading = !hasLoadedOnce || isFilterFetching;

  useEffect(() => {
    onLoadingChange?.(showListLoading);
  }, [showListLoading, onLoadingChange]);

  useEffect(() => {
    setSelectedRowIds([]);
    setSelectedItemsIds([]);
    setSelectedBackerID([]);
  }, [removeSelection]);

  const applyOrdersResponse = (response, queryKey) => {
    const payload = response?.data || null;
    setListPayload(payload);
    setOrders(payload || []);
    setHasLoadedOnce(true);
    if (queryKey) {
      dispatch(setOrdersListCache({ queryKey, payload }));
    }
  };

  // Socket-driven refresh: patch rows in place — no navigate, no loader
  useEffect(() => {
    if (!realtimeVersion || isMobile) return;

    let cancelled = false;
    const nextQuery = {
      ...(search && typeof search === "object" ? search : {}),
      page,
    };
    if (type && type !== "all" && !(search?.Status?.length > 0)) {
      nextQuery.Status = type;
    }

    silentInFlightRef.current += 1;

    (async () => {
      try {
        const response = await getOrders(nextQuery).unwrap();
        if (!cancelled) {
          const queryKey = buildOrdersListQueryKey({
            page,
            type,
            search,
            activeTab,
          });
          applyOrdersResponse(response, queryKey);
        }
      } catch (err) {
        console.error("Silent realtime orders refresh failed:", err);
      } finally {
        silentInFlightRef.current = Math.max(0, silentInFlightRef.current - 1);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when socket bumps version
  }, [realtimeVersion]);

  useEffect(() => {
    const isRealFilterChange =
      prevSearch &&
      search &&
      JSON.stringify({ ...prevSearch, page: undefined }) !==
        JSON.stringify({ ...search, page: undefined });

    let pageToUse = page;
    // Any status-tab / type change (including back to "all") must reset pagination
    if (isRealFilterChange || type !== prevType) {
      pageToUse = 1;
      setPage(1);
      dispatch(setOrdersPage(1));
      if (type !== prevType) {
        setSelectedRowIds([]);
        setSelectedItemsIds([]);
        setSelectedBackerID([]);
        setSelectedShopifyId([]);
      }
    }

    const params = new URLSearchParams(location.search);
    if (!isMobile && pageToUse !== 1) {
      params.set("page", pageToUse);
    } else {
      params.delete("page");
    }
    navigate({ search: params.toString() }, { replace: true });

    const queryParams = {
      ...search,
      page: pageToUse,
    };

    if (type && type !== "all" && !(search?.Status?.length > 0)) {
      queryParams.Status = type;
    }

    const queryKey = buildOrdersListQueryKey({
      page: pageToUse,
      type,
      search,
      activeTab,
    });
    const cachedPayload =
      ordersListCache?.queryKey === queryKey ? ordersListCache.payload : null;

    let cancelled = false;
    // Paint cached rows immediately when returning from order details; refresh in background
    if (cachedPayload) {
      setListPayload(cachedPayload);
      setOrders(cachedPayload);
      setHasLoadedOnce(true);
      setIsFilterFetching(false);
    } else {
      setIsFilterFetching(true);
    }

    const fetchAndSetOrders = async () => {
      try {
        const response = await getOrders(queryParams).unwrap();
        if (cancelled) return;
        if (isMobile && pageToUse > 1) {
          const nextPayload = mergeMobileOrdersPayload(listPayload, response?.data || null);
          setListPayload(nextPayload);
          setOrders(nextPayload || []);
          setHasLoadedOnce(true);
          dispatch(setOrdersListCache({ queryKey, payload: nextPayload }));
        } else {
          applyOrdersResponse(response, queryKey);
        }

        if (queryParams.orderSheet) {
          const uniqueOrders = response?.data?.uniqueOrders?.length || 0;
          const notFoundOrders = response?.data?.notFoundOrders || [];
          const notFoundOrdersCount = notFoundOrders.length;

          if (uniqueOrders > 0) {
            showToast(`Found ${uniqueOrders} order(s) from the sheet.`, "success");
          }
          if (notFoundOrdersCount > 0) {
            showToast(
              `${notFoundOrdersCount} order number(s) not found: ${notFoundOrders.join(", ")}`,
              "error",
              { duration: 5000 }
            );
          }
        }
      } catch (err) {
        if (!cancelled) {
          if (!cachedPayload) {
            setListPayload(null);
            setOrders([]);
          }
          setHasLoadedOnce(true);
        }
        console.error("Failed to fetch orders:", err);
      } finally {
        if (!cancelled) {
          setIsFilterFetching(false);
        }
      }
    };
    fetchAndSetOrders();
    setPrevSearch(search);
    setPrevType(type);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, type, search, activeTab, isMobile]);

  // Use uniqueOrders for display, but keep the original structure for other data like pagination.
  const allOrderItems = listPayload?.orders || [];
  const totalCounts = showItemsAndCountryColumns
    ? allOrderItems.reduce((acc, order) => {
        acc[order.BackerID] = (acc[order.BackerID] || 0) + 1;
        return acc;
      }, {})
    : {};
  const removedCounts = showItemsAndCountryColumns
    ? allOrderItems.reduce((acc, order) => {
        if (order.isRemoved) {
          acc[order.BackerID] = (acc[order.BackerID] || 0) + 1;
        }
        return acc;
      }, {})
    : {};
  const displayedOrders = (listPayload?.uniqueOrders || []).map((row) => {
    const order = resolveOrderSelectionRow(row, allOrderItems);
    return showItemsAndCountryColumns
      ? {
          ...order,
          removed_count: removedCounts[order.BackerID] || 0,
          calculated_total_count: totalCounts[order.BackerID] || 0,
        }
      : order;
  });
  const tableData = { ...listPayload, orders: displayedOrders };

  const totalPages = listPayload?.pagination?.totalPages || 0;
  const handleLoadMore = React.useCallback(() => {
    if (!isFilterFetching && page < totalPages) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [isFilterFetching, page, totalPages]);

  const handleRowSelect = (shopifyId, ItemID, BackerID, checked, shopifyOrderId) => {
    const id = shopifyId != null ? String(shopifyId) : shopifyId;
    setSelectedRowIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => String(x) !== String(shopifyId))
    );
    setSelectedItemsIds((prev) =>
      checked ? [...prev, ItemID] : prev.filter((x) => x !== ItemID)
    );
    setSelectedBackerID((prev) =>
      checked ? [...prev, BackerID] : prev.filter((x) => x !== BackerID)
    );
    setSelectedShopifyId((prev) =>
      checked ? [...prev, shopifyOrderId] : prev.filter((x) => x !== shopifyOrderId)
    );
  };

  const isRowSelectable = (row) => isOrderRowSelectable(row, allOrderItems, canSoftDelete);

  const selectedIncludesCancelled = (displayedOrders || []).some(
    (row) =>
      Number(row.Status) === 999 &&
      selectedBackerID.some((id) => String(id) === String(row.BackerID))
  );

  const handleSelectAll = (checked) => {
    const selectableOrders = (displayedOrders || []).filter(isRowSelectable);
    const currentShopifyIds = selectableOrders.map((row) => row.ShopifyItemID);
    const currentItemIds = selectableOrders.map((row) => row.ItemID);
    const currentBackerID = selectableOrders.map((row) => row.BackerID);

    const allSelectedShopifyIds = selectableOrders.map((row) => row.ShopifyId) || [];
    const allShopifyIdsOnPage = displayedOrders.map((row) => row.ShopifyItemID) || [];

    if (checked) {
      const normalizedIds = currentShopifyIds.map((x) => (x != null ? String(x) : x));
      setSelectedRowIds((prev) => [
        ...new Set([
          ...prev.map((x) => (x != null ? String(x) : x)),
          ...normalizedIds,
        ]),
      ]);
      setSelectedItemsIds((prev) => [...new Set([...prev, ...currentItemIds])]);
      setSelectedBackerID((prev) => [...new Set([...prev, ...currentBackerID])]);
      setSelectedShopifyId((prev) => [
        ...new Set([...prev, ...allSelectedShopifyIds]),
      ]);
    } else {
      // When unchecking, remove all items from the current page from the selection
      const pageIdSet = new Set(
        allShopifyIdsOnPage.map((x) => (x != null ? String(x) : x))
      );
      setSelectedRowIds((prev) =>
        prev.filter((id) => !pageIdSet.has(id != null ? String(id) : id))
      );
      const allItemIdsOnPage = displayedOrders.map((row) => row.ItemID) || [];
      setSelectedItemsIds((prev) =>
        prev.filter((id) => !allItemIdsOnPage.includes(id))
      );
      const pageBackerIdSet = new Set(
        displayedOrders.map((row) =>
          row.BackerID != null ? String(row.BackerID) : row.BackerID
        )
      );
      setSelectedBackerID((prev) =>
        prev.filter((id) => !pageBackerIdSet.has(id != null ? String(id) : id))
      );
      setSelectedShopifyId((prev) =>
        prev.filter((id) => !allSelectedShopifyIds.includes(id))
      );
    }
  };

  const handleRowClick = async (id, ItemID) => {
    const row = displayedOrders.find(
      (r) =>
        String(r?.ItemID) === String(ItemID) ||
        String(r?.ShopifyId) === String(id)
    );
    // Socket may have flagged every identity field — clear/mark all or green sticks.
    const idsToClear = [
      ItemID,
      id,
      row?.ItemID,
      row?.ShopifyItemID,
      row?.ShopifyId,
      row?.BackerID,
    ];

    try {
      await markOrderSeen({ body: { ItemID } }).unwrap();
      dispatch(markOrdersLocallySeen(idsToClear));
      dispatch(clearOrdersRealtimeUnseen(idsToClear));
      dispatch(setOrdersPage(page));
      navigate(`/orders/order-details/${id}`, {
        state: { ItemID, returnToOrdersPage: page },
      });
    } catch (error) {
      console.error("Failed to mark order as seen:", error);
      // Still allow navigation if mark-seen fails; green may persist until backend is fixed
      navigate(`/orders/order-details/${id}`, {
        state: { ItemID, returnToOrdersPage: page },
      });
    }
  };

  const handlePrintSelected = async () => {
    if (!selectedBackerID.length) {
      showToast("Select at least one order to print.", "error");
      return;
    }

    if (labelPrintInFlight.current) return;
    labelPrintInFlight.current = true;
    showToast("Preparing order labels…", "info");

    try {
      const selectedOrders = await loadSelectedOrderLabels(
        selectedBackerID,
        (params) => getOrdersForPrint(params).unwrap()
      );
      const { generateOrderLabelsPdf } = await import("../../helpers/OrderLabelPdf");
      await generateOrderLabelsPdf(selectedOrders);
      showToast(`${selectedOrders.length} order label(s) sent to print preview.`, "success");
    } catch (error) {
      console.error("Failed to generate order label PDF:", error);
      showToast(getOrderLabelErrorMessage(error), "error");
    } finally {
      labelPrintInFlight.current = false;
    }
  };

  /**
   * Green dot = unseen order.
   * Priority: local seen (after open) → backend isNewOrder false → backend true → socket unseen flags.
   */
  const isOrderUnseen = (row) => {
    const keys = [
      row?.ItemID,
      row?.ShopifyItemID,
      row?.ShopifyId,
      row?.BackerID,
    ]
      .filter((value) => value !== undefined && value !== null && value !== "")
      .map((value) => String(value));

    if (keys.some((key) => realtimeSeenIds.includes(key))) return false;
    if (row?.isNewOrder === false) return false;
    if (row?.isNewOrder === true) return true;
    return keys.some((key) => realtimeUnseenIds.includes(key));
  };

  const approvalColumn = {
    label: "Approval",
    accessor: "distributorApprovalStatus",
    sortable: true,
    columnClass: "orders-col-approval",
    getSortValue: (row) => (row?.distributorApprovalStatus === "approved" ? 1 : 0),
    cell: ({ row }) => {
      if (isDemoOrderRow(row, allOrderItems)) {
        return "—";
      }
      const status = row?.distributorApprovalStatus;
      if (status !== "approved" && status !== "pending") {
        return "—";
      }
      const approved = status === "approved";
      return (
        <span className={`status-badge ${approved ? "class-shipped" : "class-readyShip"}`}>
          {approved ? "Approved" : "Pending"}
        </span>
      );
    },
  };

  const columns = [
    {
      label: (
        <input
          type="checkbox"
          disabled={!canBulkSelect}
          checked={(() => {
            if (!canBulkSelect) return false;
            const selectable = displayedOrders.filter(isRowSelectable);
            return selectable.length > 0 && selectable.every((row) =>
              selectedBackerID.some((id) => String(id) === String(row.BackerID))
            );
          })()}
          onChange={(e) => handleSelectAll(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      accessor: "checkbox",
      sortable: false,
      columnClass: "orders-col-checkbox",
      resizable: false,
      cell: ({ row }) => {
        const notSelectable = !isRowSelectable(row);
        return (
          <input
            type="checkbox"
            disabled={!canBulkSelect || notSelectable}
            value={row.ShopifyItemID}
            checked={
              canBulkSelect &&
              !notSelectable &&
              selectedBackerID.some((id) => String(id) === String(row.BackerID))
            }
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              handleRowSelect(
                row.ShopifyItemID,
                row.ItemID,
                row.BackerID,
                e.target.checked,
                row.ShopifyId
              );
            }}
          />
        );
      },
    },
    {
      label: "Order ID",
      accessor: "BackerID",
      sortable: true,
      getSortValue: (row) =>
        row.BackerID == null ? "" : String(row.BackerID),
      cell: ({ row }) => {
        const demo = isDemoOrderRow(row, allOrderItems);
        const demoLabel = getDemoOptionLabel(
          row?.demoOptionCode ||
            allOrderItems.find(
              (item) =>
                String(item.BackerID) === String(row?.BackerID) &&
                item.isDemo === true
            )?.demoOptionCode
        );
        return (
          <div className="rowId d-flex gap-2 align-items-center">
            <span
              className={`green-dot shrink-0${isOrderUnseen(row) ? "" : " opacity-0"}`}
              aria-hidden={!isOrderUnseen(row)}
            />
            <span
              className={`order-id-with-demo${demo ? " has-demo" : ""}`}
              title={row.BackerID != null ? String(row.BackerID) : ""}
            >
              {row.BackerID}
              {demo && (
                <span
                  className="status-badge class-demo class-demo--corner"
                  title={`Demo unit — ${demoLabel}`}
                >
                  Demo
                </span>
              )}
            </span>
          </div>
        );
      },
    },
    {
      label: "Order created",
      accessor: "OrderCreatedAt",
      sortable: true,
      getSortValue: (row) => getOrderCreatedDate(row, allOrderItems)?.getTime() ?? "",
      cell: ({ row }) => {
        const date = getOrderCreatedDate(row, allOrderItems);
        return (
          <span className="text-nowrap" title={date ? `${date.toISOString()} · displayed in your local time` : "Order creation time unavailable"}>
            {date ? <time dateTime={date.toISOString()}>{formatOrderCreatedDate(date)}</time> : "—"}
          </span>
        );
      },
    },
    {
      label: nameColumnLabel,
      accessor: "customerName",
      sortable: true,
      getSortValue: (row) => row?.customerName || "",
      cell: ({ row }) => (
        <span className="text-nowrap" title={row?.customerName || ""}>
          {row?.customerName || ""}
        </span>
      ),
    },
    {
      label: "Provider name",
      accessor: "providerName",
      sortable: true,
      getSortValue: (row) => getProviderNameForRow(row, allOrderItems),
      cell: ({ row }) => {
        const providerName = getProviderNameForRow(row, allOrderItems);
        if (!providerName) return "—";
        return (
          <span className="text-nowrap" title={providerName}>
            {providerName}
          </span>
        );
      },
    },

    {
      label: "Status",
      accessor: "Status",
      sortable: true,
      columnClass: "orders-col-status",
      getSortValue: (row) => {
        const orderItems = allOrderItems.filter(
          (item) => item.BackerID === row.BackerID
        );
        const isAllReturned =
          orderItems.length > 0 && orderItems.every((item) => item.isReturned);
        // Returned after status 999 so sort order matches displayed status codes
        if (isAllReturned) return 1000;
        const statusNum = Number(row.Status);
        return Number.isFinite(statusNum) ? statusNum : -1;
      },
      cell: ({ row }) => {
        const orderItems = allOrderItems.filter(
          (item) => item.BackerID === row.BackerID
        );
        const isAllReturned =
          orderItems.length > 0 && orderItems.every((item) => item.isReturned);

        if (isAllReturned) {
          return <span className="class-cancelled status-badge">Returned</span>;
        }
        const status = getClassByType(row.Status);
        return (
          <span className={`${status.class} status-badge`}>
            <span className="borderBar pe-2 me-1">
              {row.Status === 999 ? "999" : `0${row.Status}`}
            </span>
            {status.title}
          </span>
        );
      },
    },
    ...(showApprovalStatusColumn ? [approvalColumn] : []),
    {
      label: "Captify Version",
      accessor: "CaptifyVersion",
      sortable: true,
      getSortValue: (row) => getCaptifyVersionLabel(row, allOrderItems) || "",
      cell: ({ row }) => {
        const version = getCaptifyVersionLabel(row, allOrderItems);
        if (!version) return "---";
        return <strong title={version}>{version}</strong>;
      },
    },
    {
      label: "PO number",
      accessor: "invoicePoNumber",
      sortable: true,
      getSortValue: (row) => getPoNumberForRow(row, allOrderItems),
      cell: ({ row }) => {
        const poNumber = getPoNumberForRow(row, allOrderItems);
        if (!poNumber) return "—";
        return (
          <span className="text-nowrap" title={poNumber}>
            {poNumber}
          </span>
        );
      },
    },
    {
      label: "Notes",
      accessor: "Note",
      sortable: true,
      getSortValue: (row) => row.Note || "",
      cell: ({ row }) => (
        <span title={row.Note || ""}>{row.Note || ""}</span>
      ),
    },
    ...(showItemsAndCountryColumns
      ? [
          {
            label: "Items",
            accessor: "totalLineItemsQuantity",
            sortable: true,
            getSortValue: (row) => {
              const total =
                Number(row.totalLineItemsQuantity) ||
                row.calculated_total_count ||
                0;
              const removed = row.removed_count || 0;
              const result = total - removed;
              return result < 0 ? total : result;
            },
            cell: ({ row }) => {
              const total =
                Number(row.totalLineItemsQuantity) ||
                row.calculated_total_count ||
                0;
              const removed = row.removed_count || 0;
              const result = total - removed;
              return <span>{result < 0 ? total : result}</span>;
            },
          },
          {
            label: "Country",
            accessor: "AddressCountryCode",
            sortable: true,
            columnClass: "orders-col-country",
            getSortValue: (row) => row.AddressCountryCode || "",
            cell: ({ row }) =>
              row.AddressCountryCode ? (
                <div className="d-flex align-items-center gap-2">
                  <CountryFlag
                    countryCode={row.AddressCountryCode}
                    svg
                    style={{
                      width: "1.5em",
                      height: "1.5em",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                    title={row.AddressCountryCode}
                  />
                  <span>{row.AddressCountryCode}</span>
                </div>
              ) : (
                "—"
              ),
          },
        ]
      : []),
  ];

  return (
    <div>
      {canBulkSelect &&
        (selectedRowIds.length > 0 || selectedItemsIds.length > 0) && (
          <OrderFilters
            setShowFilter={setShowFilter}
            rowCount={selectedRowIds.length}
            selectedIds={selectedRowIds}
            selectedShopifyIds={selectedShopifyId}
            selectedItems={selectedItemsIds}
            selectedBackerID={selectedBackerID}
            setSelectedRow={setSelectedRow}
            onSelectPage={handleSelectAll}
            onPrint={handlePrintSelected}
            selectedIncludesCancelled={selectedIncludesCancelled}
            onClearSelection={() => {
              setSelectedRowIds([]);
              setSelectedItemsIds([]);
              setSelectedBackerID([]);
              setSelectedShopifyId([]);
            }}
          />
        )}
      {canViewOrders && (
        <Datatable
          selectedRow={selectedRow}
          tableData={tableData}
          columns={columns}
          showPegination
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          isLoading={showListLoading}
          handleRowClick={handleRowClick}
          resizableColumns
          loadMoreMode={isMobile}
          onLoadMore={handleLoadMore}
          hasMore={page < totalPages}
          isLoadingMore={isFilterFetching}
          className={
            variant === "distributor"
              ? "va-distributor-orders-table"
              : variant === "audiologist"
                ? "va-audiologist-orders-table"
                : ""
          }
        />
      )}
      {showListLoading && !listPayload && <Loader />}
    </div>
  );
};

export default OrdersList;
